import { QuestionAnswerMap } from '@care-portals/api-interfaces';

const weightMngInitialQuestions = [
  {
    field: 'weightMng_genderIdentify',
    label: 'What was your sex assigned at birth?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    }
  },
  {
    field: 'weightMng_pregnancyStatus',
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'weightMng_consentPregnancy',
    label: 'Consent (pregnancy):',
    type: 'select',
    educationalSlide:
      'I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus.\n\nI agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.\n\nI understand that I should stop taking this medication before attempting to become pregnant. I agree to consult with my healthcare provider prior to discontinuing the medication and before planning a pregnancy to ensure my safety and well-being.\n\nBy selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the information and do NOT wish to proceed',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information, I understand the risks and wish to proceed',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    // TODO if BMI is under 27 for a treatment naive patient → disqualified; if BMI is under 23 for a patient currently on treatment → disqualified
    label: 'What is your:',
    field: 'weightMng_weightHeightBMI',
    type: 'info',
    required: false,
    answer: {
      type: 'bmi',
      showLabel: true,
      bmiLabel: 'Your BMI is',
      heightLabel: 'What is your height in feet and inches?',
      weightLabel: 'What is your weight in pounds?'
    }
  },

  {
    field: 'weightMng_consentBmi',
    label: 'Consent (BMI):',
    type: 'select',
    educationalSlide:
      'The traditional use of weight loss medications is for individuals with a BMI of 30 and above or to those who are overweight who have associated health conditions. Using it for someone with a BMI range (25-29) without an accompanying health condition is termed "off-label." Using a medication "off-label" refers to the practice of prescribing a drug for a purpose, age group, dosage, or form of administration that is not included in the approved labeling by regulatory agencies like the U.S. Food and Drug Administration (FDA). While a medication undergoes rigorous testing for specific uses before receiving approval, healthcare providers may discover through clinical experience or research that it can be effective for treating other conditions. There may be benefits such as weight reduction for individuals within your range. If you agree to this off-label use, it\'s crucial to follow the prescribed regimen and report any concerns. Please discuss any questions with us.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I acknowledge that I have read and understood the above information',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'weightMng_startingWeight',
    label:
      'What was your starting weight or highest weight that you have reached?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'weightMng_startingWeight'
    }
  },
  {
    field: 'weightMng_diagnosisTime',
    label: 'When were you first diagnosed with overweight or obesity?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Within the last year', key: 'within_year' },
        { value: '1–5 years ago', key: 'one_to_five_years' },
        { value: 'More than 5 years ago', key: 'more_than_five_years' },
        { value: 'Unsure', key: 'unsure' }
      ]
    }
  },
  {
    field: 'weightMng_previousTreatment',
    label:
      'Have you previously received any treatment for your weight management?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'weightMng_treatmentTypes',
    label: 'Which type(s) of treatment have you received?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Lifestyle modifications', key: 'lifestyle' },
        { value: 'Pharmacotherapy', key: 'pharmacotherapy' },
        { value: 'Bariatric surgery', key: 'bariatric_surgery' },
        { value: 'Other', key: 'other' }
      ]
    }
  },
  {
    field: 'weightMng_otherTreatment',
    label: 'Tell us more about your other treatment',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightMng_otherTreatment'
    }
  },
  {
    field: 'weightMng_treatmentComplications',
    label:
      'Did you experience any complications with your previous weight management treatment?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' },
        { value: 'Not applicable', key: 'not_applicable' }
      ]
    }
  },
  {
    field: 'weightMng_complicationsDetails',
    label: 'Tell us more about these complications',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightMng_complicationsDetails'
    }
  },
  {
    field: 'weightMng_currentApproach',
    label: 'How would you describe your current approach to weight management?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Actively managing', key: 'actively_managing' },
        { value: 'Some efforts', key: 'some_efforts' },
        { value: 'No active efforts', key: 'no_active_efforts' }
      ]
    }
  },
  {
    field: 'weightMng_weightSymptoms',
    label:
      'Are you currently experiencing any weight-related symptoms or complications such as: (Check all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Hypertension (high blood pressure)', key: 'hypertension' },
        {
          value: 'Dyslipidemia (high cholesterol or triglycerides)',
          key: 'dyslipidemia'
        },
        {
          value: 'Coronary artery disease (CAD)',
          key: 'coronary_artery_disease'
        },
        { value: 'Obstructive sleep apnea', key: 'sleep_apnea' },
        { value: 'Osteoarthritis', key: 'osteoarthritis' },
        {
          value: 'Mobility issues which are impacted by body weight',
          key: 'mobility_issues'
        },
        {
          value:
            'Gastroesophageal reflux disease (GERD) related to body weight',
          key: 'gerd'
        },
        { value: 'Metabolic Syndrome', key: 'metabolic_syndrome' },
        { value: 'Fatty liver disease', key: 'fatty_liver' },
        { value: 'Prediabetes', key: 'prediabetes' },
        { value: 'Type 2 Diabetes', key: 'type2_diabetes' },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightMng_activityLevel',
    label: 'How would you rate your current physical activity level?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: "Sedentary (I don't exercise)", key: 'sedentary' },
        { value: 'Light', key: 'light' },
        { value: 'Moderate', key: 'moderate' },
        { value: 'Vigorous', key: 'vigorous' }
      ]
    }
  },
  {
    field: 'weightMng_dietaryModifications',
    label: 'What dietary modifications, if any, are you currently following?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'None', key: 'none' },
        { value: 'Minor modifications', key: 'minor_modifications' },
        { value: 'Significant dietary changes', key: 'significant_changes' }
      ]
    }
  },
  {
    field: 'weightMng_metforminContraindications',
    label: 'Do you have any of the following? (Check all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Renal impairment (CKD stage 3b, 4, or 5)',
          key: 'renal_impairment'
        },
        {
          value: 'History of hypersensitivity to metformin',
          key: 'metformin_hypersensitivity'
        },
        {
          value: 'History of acute or chronic metabolic acidosis',
          key: 'metabolic_acidosis'
        },
        {
          value: 'CURRENT or recurrent vitamin B12 deficiency',
          key: 'b12_deficiency'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightMng_bupropionContraindications',
    label: 'Do you have any of the following? (Check all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'History of seizure disorder', key: 'seizure_disorder' },
        {
          value: 'History of bulimia or anorexia nervosa',
          key: 'eating_disorder'
        },
        {
          value:
            'Recent abrupt discontinuation of alcohol, benzodiazepines, barbiturates, or antiepileptic medications',
          key: 'abrupt_discontinuation'
        },
        {
          value:
            'Use of monoamine oxidase inhibitors (MAOIs) within the past 14 days',
          key: 'maoi_use'
        },
        {
          value:
            'History of hypersensitivity to bupropion or any of its ingredients',
          key: 'bupropion_hypersensitivity'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightMng_naltrexoneContraindications',
    label: 'Do you have any of the following? (Check all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Currently receiving opioid pain medication',
          key: 'opioid_medication'
        },
        {
          value: 'Current physiologic opioid dependence',
          key: 'opioid_dependence'
        },
        { value: 'Acute opioid withdrawal', key: 'opioid_withdrawal' },
        {
          value: 'History of hypersensitivity to naltrexone (or VIVITROL)',
          key: 'naltrexone_hypersensitivity'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightMng_acarboseContraindications',
    label: 'Do you have any of the following? (Check all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value:
            'History of hypersensitivity to acarbose or any of its components',
          key: 'acarbose_hypersensitivity'
        },
        {
          value: 'History of diabetic ketoacidosis',
          key: 'diabetic_ketoacidosis'
        },
        {
          value: 'History of liver cirrhosis or elevated liver enzymes',
          key: 'liver_issues'
        },
        {
          value: 'History of inflammatory bowel disease or colonic ulceration',
          key: 'bowel_disease'
        },
        {
          value: 'History of partial or complete intestinal obstruction',
          key: 'intestinal_obstruction'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightMng_orlistatContraindications',
    label: 'Do you have any of the following? (Check all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value:
            'History of chronic malabsorption syndrome (for example, chronic diarrhea, celiac disease, inflammatory bowel disease, or history of bariatric surgery)',
          key: 'malabsorption_syndrome'
        },
        {
          value:
            'History of cholestasis (reduced or blocked bile flow from the liver)',
          key: 'cholestasis'
        },
        {
          value:
            'History of hypersensitivity to orlistat or any of its components',
          key: 'orlistat_hypersensitivity'
        },
        {
          value:
            'Currently taking medications known to interact with orlistat (such as cyclosporine, levothyroxine, seizure medications, or warfarin)',
          key: 'orlistat_interactions'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightMng_sglt2Contraindications',
    label: 'Do you have any of the following? (Check all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Recurrent issues with dehydration',
          key: 'dehydration_issues'
        },
        { value: 'Issues with low blood pressure', key: 'low_blood_pressure' },
        {
          value:
            'Allergic reactions or side effects to SGLT2 products like Jardiance, Farxiga, Invokana',
          key: 'sglt2_allergies'
        },
        { value: 'Recurrent urinary tract infections', key: 'recurrent_uti' },
        { value: 'Recurrent yeast infection', key: 'recurrent_yeast' },
        {
          value: 'History of necrotizing fasciitis',
          key: 'necrotizing_fasciitis'
        },
        {
          value:
            'History of peripheral arterial disease (decreased blood flow to extremities)',
          key: 'peripheral_arterial_disease'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightMng_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightMng_additionalQuestions'
    }
  },
  {
    field: 'weightMng_treatmentConsent',
    label: 'Consent (Treatment with Weight Management Pharmacotherapy):',
    type: 'select',
    educationalSlide:
      "Please read the following information to understand the potential benefits and risks. These medications are designed to support the healthy lifestyle changes you are making. Aim to limit processed foods, particularly those high in added sugars, and try to avoid consuming calories through beverages such as soda, juice, or alcohol unless you're having nutrient-rich smoothies. Eating smaller, well-portioned meals is recommended. Keep in mind that even though artificial sweeteners are marketed as \"zero calories,\" they have been linked to an increase in waist size. It's important to engage in exercise on most days, accumulating at least 150 minutes of activity per week, spread out over those days. Since weight loss can result in the loss of not just fat, but also muscle and bone, incorporate both cardiovascular and resistance training into your routine to help build and maintain muscle and bone strength. Additionally, stay well hydrated, as dehydration is a potential side effect of weight loss medications, and ensure you get adequate sleep to support both your physical and mental well-being.\n\nIndication for Use:\nYou are considering treatment with one or more weight management medications, which may include metformin, bupropion, naltrexone, acarbose, SGLT2 inhibitors, and/or orlistat, as part of your personalized plan for managing overweight or obesity. These medications work through different mechanisms—improving insulin sensitivity, modulating appetite and mood, increasing glucose excretion through urine, reducing carbohydrate absorption, or inhibiting fat absorption—to help achieve weight loss and improve metabolic health.\n\nPotential Benefits:\nAchievement of weight loss or more effective weight management\nImproved blood glucose control and insulin sensitivity\nEnhanced overall metabolic health\nPotential reduction in cardiovascular risk factors\n\nPotential Side Effects:\nMetformin\n Common: Gastrointestinal upset (nausea, vomiting, diarrhea), decreased appetite\n Serious (rare): Lactic acidosis, vitamin B12 deficiency over long-term use\n\nBupropion\n Common: Dry mouth, headache, insomnia, nausea, anxiety\n Serious: Increased risk of seizures (especially in individuals with predisposing factors), elevated blood pressure\n\nNaltrexone\n Common: Nausea, headache, dizziness, fatigue\n Serious: Potential liver enzyme elevations or liver injury at high doses\n\nAcarbose\n Common: Gastrointestinal discomfort including flatulence, diarrhea, bloating, and abdominal pain\n Serious: Rare severe gastrointestinal events\n\nSGLT2 Inhibitors (e.g., canagliflozin, dapagliflozin, empagliflozin)\n Common: Increased urination, genital yeast infections, urinary tract infections, mild dehydration\n Serious: Risk of diabetic ketoacidosis (rare, even in non-diabetics), Fournier's gangrene (a rare serious genital infection), volume depletion, increased risk of lower limb amputation (mainly with canagliflozin in some studies)\n\nOrlistat\n Common: Oily spotting, increased frequency of bowel movements, flatulence with discharge, possible reduction in absorption of fat-soluble vitamins\n Serious: Rare reports of liver injury\n\nRisks and Considerations:\n Certain conditions or medical histories may make one or more of these medications unsuitable.\nMetformin is contraindicated in severe kidney impairment or a history of metabolic acidosis.\nBupropion should be avoided in those with seizure disorders or a history of eating disorders\nNaltrexone is not recommended for patients using opioids or with opioid dependence. Please note, this is being prescribed specifically for weight management and not for any addiction-related diagnosis. Some life or disability insurance providers may see naltrexone use as a flag for prior substance use. If you are applying for insurance soon, consider completing that process before starting this medication\nAcarbose should not be used in individuals with inflammatory bowel disease, colonic ulceration, or a history of intestinal obstruction.\nSGLT2 inhibitors are not recommended in individuals with a history of frequent urinary tract infections, recurrent yeast infections, or those prone to dehydration. They should be used cautiously in individuals with kidney disease or low blood pressure.\nOrlistat is contraindicated in those with chronic malabsorption syndromes or cholestasis.\n\nMonitoring and Follow-Up:\nOngoing monitoring is essential; follow-up visits will be scheduled to assess treatment efficacy and review any side effects. In some cases, additional assessments (such as digital photographs to verify weight changes) may be requested to ensure consistency in reporting.\n\nBy selecting one of the options below, you acknowledge that you have read and understand the potential benefits, side effects, risks, and monitoring requirements associated with weight management pharmacotherapy and that you consent to proceed with the treatment plan as discussed.",
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read and understand the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information and I wish to continue',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'weightMng_truthfulnessConsent',
    label: 'Consent (Truthfulness):',
    type: 'select',
    educationalSlide:
      'Please attest to the following confirming that all information you have provided to us is true and complete.\n\nI verify that I am the patient and that I have answered the questions asked in this intake form.  I confirm that I have reviewed and understood all the questions asked of me.  I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor.  I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'weightMng_complete',
    isLast: true,
    label: `Thank you for completing this questionnaire, please select "Finish" to submit`,
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'weightMng_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    answer: {},
    blockQuestionFlow: true,
    type: 'info',
    isLast: true
  }
] as const;

export type WeightManagementAnswerField = Partial<
  QuestionAnswerMap<typeof weightMngInitialQuestions>
>;

export default weightMngInitialQuestions;